import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DropDownSelect from '../../../components/shared/DropdownSelect';
import Pagination from '../../../components/shared/Pagination';
import SearchInput from '../../../components/shared/SearchInput';
import ForumMembersList from './ForumMembersList';

import { usePaginatedDropdown } from '../../../hooks/usePaginatedDropdown';
import { DropDownItemInterface, PermissionsEnum } from '../../../types';

import ForumMembersStore from '../services/forumMembers.store';
import OrganizationsStore from '../../organizations/services/organizations.store';
import NoSearchResultsInfoMessage from '../../../components/shared/InfoMessage/NoSearchResults';
import NoResultsInfoMessage from '../../../components/shared/InfoMessage/NoResults';
import TransitionLoader from '../../../components/shared/TransitionLoader/TransitionLoader';

import { usePermission } from '../../../hooks/usePermission';
import { useRegions } from '../../../context/regions-context';
import { ForumMembersFiltersProps } from '../services/forumMembers.types';

const forumMembersStore = new ForumMembersStore();
const organizationsStore = new OrganizationsStore();

const USER_STATUS_TRANSLATION_KEYS = [
  { id: '1', key: 'status.activated' },
  { id: '0', key: 'status.deactivated' },
  { id: '2', key: 'status.pending' },
];

const ForumMembers = ({
  onExtractFilters,
}: {
  onExtractFilters?: (filtersData: ForumMembersFiltersProps) => void;
}) => {
  const { t } = useTranslation();
  const { activeRegion } = useRegions();

  // Permissions
  const canListOrganizations = usePermission([
    PermissionsEnum.OrganizationsList,
  ]);
  const canToggleUserStatus = usePermission([PermissionsEnum.UserActivate]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showActive, setShowActive] = useState(true);

  //Parse URL search parameters
  const page = Number(searchParams.get('page')) || 1;

  const organization = searchParams.get('organizationIds[]') || '';
  const status = (
    ['0', '1', '2'].includes(searchParams.get('status') || '')
      ? Number(searchParams.get('status'))
      : 1
  ) as 0 | 1 | 2;

  const [searchValue, setSearchValue] = useState(
    searchParams.get('search') || ''
  );

  // FORUM MEMBERS
  const {
    data: forumMembersQueryData,
    isLoading,
    isFetching,
  } = forumMembersStore.useGetForumMembers({
    page: page,
    searchTerm: searchValue,
    organization,
    status: status,
  });
  const forumMembers = forumMembersQueryData?._embedded?.users;
  const currentPage = forumMembersQueryData?._page || 1;
  const totalPages = forumMembersQueryData?._page_count || 1;

  useEffect(() => {
    if (status === 0) {
      setShowActive(false);
      return;
    }

    setShowActive(true);
  }, [activeRegion?.id, status]);

  // STATUS
  const transformedUserStatusOptions: DropDownItemInterface[] =
    USER_STATUS_TRANSLATION_KEYS.map((option) => ({
      id: option.id,
      label: t(option.key),
    }));

  const selectedStatusIndex = transformedUserStatusOptions.findIndex(
    (option) => option.id === status.toString()
  );

  // ORGANIZATIONS
  const useOrganizationsDropdown = () => {
    return usePaginatedDropdown({
      fetchData: (page) =>
        organizationsStore.useGetOrganizations({
          page: page,
          searchTerm: '',
          enabled: canListOrganizations,
        }),
      extractItems: (data) => {
        return (data?.organizations || []).map((org: any) => ({
          id: org.id.toString(),
          label: org.name,
        }));
      },
    });
  };

  const {
    finalItems: finalOrganizations,
    isLoadingRef: isLoadingOrganizations,
  } = useOrganizationsDropdown();

  const organizationsOptions: DropDownItemInterface[] = [
    {
      id: 'all',
      label: t('institutions.all_institutions'),
    },
    ...finalOrganizations,
  ];

  const selectedOrganizationIndex = organizationsOptions.findIndex(
    (option) => option.id === organization
  );

  useEffect(() => {
    if (onExtractFilters && activeRegion) {
      const organizationName =
        organization !== ''
          ? finalOrganizations.find(
              (item) => organization === item.id.toString()
            )?.label
          : undefined;

      const statusKey = USER_STATUS_TRANSLATION_KEYS.find(
        (statusOption) => statusOption.id === status.toString()
      )?.key;

      const statusName = statusKey ? t(statusKey) : undefined;

      onExtractFilters({
        status: { id: status, name: statusName },
        organization: {
          id: organization,
          name: organizationName,
        },
        searchTerm: searchValue,
      });
    }
  }, [
    activeRegion,
    finalOrganizations,
    onExtractFilters,
    organization,
    searchValue,
    status,
    t,
  ]);

  /* Handlers */

  const handlePageChange = (newPage: number) => {
    if (newPage !== currentPage) {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set('page', newPage.toString());
        return newParams;
      });
    }
  };

  const handleSearch = (debouncedValue: string): void => {
    const trimmedValue = debouncedValue.trim();
    setSearchValue(trimmedValue);

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set('page', '1');

      if (trimmedValue.length > 0) {
        newParams.set('search', debouncedValue);
      } else {
        newParams.delete('search');
      }

      return newParams;
    });
  };

  const handleFilterChange = (selectedFilter: string, paramName: string) => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.set('page', '1');

      if (selectedFilter === 'all') {
        newSearchParams.delete(paramName);
      } else {
        newSearchParams.set(paramName, selectedFilter);
      }

      return newSearchParams;
    });
  };

  const handleOrganizationChange = (selectedOrganization: string) => {
    handleFilterChange(selectedOrganization, 'organizationIds[]');
  };

  const handleStatusChange = (selectedStatus: string) => {
    if (!canToggleUserStatus) return;
    handleFilterChange(selectedStatus, 'status');
  };

  const handleOrganizationDelete = () => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete('organizationIds[]');
      newSearchParams.set('page', '1');
      return newSearchParams;
    });
  };

  return (
    <>
      {(isLoading || (isFetching && !searchValue)) && <TransitionLoader />}
      <div className="pks-layout-col-md ">
        <div className=" flex lg:flex-row flex-col items-center lg:gap-8 gap-1 mb-2 w-12/12">
          {canToggleUserStatus && (
            <span className="lg:w-4/12 w-full">
              <DropDownSelect
                label={t('forum_members.choose_status')}
                options={transformedUserStatusOptions}
                onChange={handleStatusChange}
                selectedOptionIndex={
                  selectedStatusIndex > -1 ? selectedStatusIndex : 0
                }
              />
            </span>
          )}
          {canListOrganizations && (
            <span className="lg:w-4/12 w-full">
              <DropDownSelect
                label={t('institutions.choose_institution')}
                options={organizationsOptions}
                isLoading={isLoadingOrganizations.current}
                onRemoveFilter={handleOrganizationDelete}
                onChange={handleOrganizationChange}
                selectedOptionIndex={
                  selectedOrganizationIndex > -1 ? selectedOrganizationIndex : 0
                }
              />
            </span>
          )}
        </div>
        <SearchInput
          value={searchValue}
          onSearch={handleSearch}
          autofocus
          isFetching={isFetching}
        />
        {!isFetching && forumMembers?.length === 0 ? (
          searchValue === '' ? (
            <NoResultsInfoMessage />
          ) : (
            <NoSearchResultsInfoMessage />
          )
        ) : (
          <>
            <ForumMembersList
              forumMembers={forumMembers}
              showToggle={canToggleUserStatus && !showActive ? true : false}
            />
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ForumMembers;
